import { soDigitos } from "./formato";

// ---------------------------------------------------------------------------
// COMUNICAÇÃO COM O CLIENTE -- UM MODULO, UM TIPO POR EVENTO
//
// Regra estrutural deste arquivo, e a razão de ele existir:
//
//   montarMensagem(TIPO, dados, oficina)   -- e NÃO montar(entidade)
//
// Cada evento tem seu próprio molde. Não existe template genérico com "if"
// dentro decidindo o que dizer.
//
// CONTEÚDO NO PRÓPRIO TEXTO: pré-orçamento e comprovante levam os itens e o
// preço no corpo da mensagem, não um link. Não existe rota pública de
// documento no sistema, e uma mensagem que anuncia um documento inexistente
// é pior que nenhuma mensagem. Quando essa rota existir, cada molde ganha a
// linha do link sem que nada aqui precise ser reescrito.
//
// ┌─ REGRA INVIOLÁVEL ──────────────────────────────────────────────────────┐
// │ Mensagem que vai para o cliente mostra SOMENTE PREÇO FINAL.             │
// │ Nunca custo, nunca margem, nunca percentual de lucro. Os itens da O.S.  │
// │ carregam "custoUnit"; nenhum molde daqui lê esse campo.                 │
// └─────────────────────────────────────────────────────────────────────────┘
//
// MODELO PRÓPRIO DA OFICINA: cada oficina pode reescrever o texto de cada
// tipo, usando variáveis como {cliente} e {total}. O modelo fica guardado no
// navegador daquela oficina (ver modeloSalvo/salvarModelo). Sem modelo
// salvo, vale o molde padrão abaixo.
// ---------------------------------------------------------------------------

// Número no formato que o WhatsApp exige: 55 + DDD + número, só dígitos.
// Devolve null quando o cadastro não tem número utilizável -- é o que
// impede o dono de clicar, abrir um WhatsApp vazio e achar que enviou.
//
// O corte do "55" inicial só acontece com 12 ou 13 dígitos. Um número de
// 11 dígitos começando com 55 é DDD 55 (Rio Grande do Sul), não código de
// país: cortar ali destruiria o número.
export function telefoneWhats(bruto) {
  let d = soDigitos(bruto || "");
  if ((d.length === 12 || d.length === 13) && d.indexOf("55") === 0) d = d.slice(2);
  if (d.length === 10 || d.length === 11) return "55" + d;
  return null;
}

// --- formatação -------------------------------------------------------------

export function moeda(v) {
  const n = Number(v) || 0;
  return "R$ " + n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Lista de itens em texto. Lê SOMENTE nome, qtd e preço -- ver regra acima.
function listaItens(itens) {
  const l = Array.isArray(itens) ? itens : [];
  return l
    .map((i) => {
      const nome = String((i && i.nome) || "").trim() || "Item";
      const qtd = Number(i && i.qtd) || 1;
      const preco = Number(i && i.preco) || 0;
      return "• " + nome + (qtd > 1 ? " (x" + qtd + ")" : "") + " — " + moeda(preco);
    })
    .join("\n");
}

function totalItens(itens) {
  const l = Array.isArray(itens) ? itens : [];
  return l.reduce((a, i) => a + (Number(i && i.preco) || 0), 0);
}

// --- peças compartilhadas ---------------------------------------------------

function nomeOficina(of) {
  return ((of && of.nomeEmpresa) || "").trim();
}

function saudacao(dados, of) {
  const nomeOf = nomeOficina(of);
  const nome = (dados.clienteNome || "").trim();
  return "Olá, " + nome + "!" + (nomeOf ? " Aqui é da " + nomeOf + "." : "");
}

function assinatura(of) {
  const nomeOf = nomeOficina(of);
  if (!nomeOf) return null;
  const tel = ((of && of.telefone) || "").trim();
  return nomeOf + (tel ? " · " + tel : "");
}

function linhaVeiculo(dados) {
  const v = [dados.veiculo, dados.placa].filter(Boolean).join(" · ");
  return v || null;
}

// --- tipos ------------------------------------------------------------------
// Cada entrada: { rotulo, variaveis, montar(dados, of) -> string }

export const TIPOS = {

  // ── SOLICITAÇÃO RECEBIDA ─────────────────────────────────────────────────
  // Serve o momento em que o cliente pediu e ainda NÃO há data marcada.
  // Antes deste tipo existir, a tela escondia o botão nesse estado -- tratar
  // "sem data" como "sem comunicação" era modelar por entidade, não por
  // evento, que é justamente o erro que este módulo existe para evitar.
  agendamento_solicitacao: {
    rotulo: "Solicitação recebida",
    variaveis: ["cliente", "oficina", "veiculo", "placa", "servico", "endereco", "telefone_oficina"],
    montar(dados, of) {
      const l = [];
      l.push(saudacao(dados, of));
      l.push("");
      l.push("Recebemos sua solicitação de atendimento.");
      l.push("");
      const veic = linhaVeiculo(dados);
      if (veic) l.push("Veículo: " + veic);
      if ((dados.servico || "").trim()) l.push("Serviço: " + dados.servico.trim());
      l.push("");
      l.push("Vamos verificar a agenda e já retornamos por aqui com o horário disponível.");
      l.push("");
      l.push("Se precisar adiantar alguma informação, é só responder esta mensagem.");
      const ass = assinatura(of);
      if (ass) l.push(ass);
      return l.join("\n");
    }
  },

  // ── AGENDAMENTO CONFIRMADO ───────────────────────────────────────────────
  // Texto validado com cliente real. NÃO alterar sem necessidade.
  agendamento_confirmado: {
    rotulo: "Agendamento confirmado",
    variaveis: ["cliente", "oficina", "data", "veiculo", "placa", "servico", "endereco", "telefone_oficina"],
    montar(dados, of) {
      const l = [];
      l.push(saudacao(dados, of));
      l.push("");
      l.push("Seu agendamento está confirmado:");
      l.push("");
      l.push("Data: " + (dados.dataHoraTexto || ""));
      const veic = linhaVeiculo(dados);
      if (veic) l.push("Veículo: " + veic);
      if ((dados.servico || "").trim()) l.push("Serviço: " + dados.servico.trim());
      const end = ((of && of.endereco) || "").trim();
      if (end) l.push("Endereço: " + end);
      l.push("");
      l.push("Pedimos a gentileza de chegar com 10 minutos de antecedência.");
      l.push("Se precisar remarcar ou não puder comparecer, avise com pelo menos 5 horas de antecedência — assim conseguimos atender outro cliente no seu horário.");
      l.push("");
      l.push("Qualquer dúvida, é só responder por aqui.");
      const ass = assinatura(of);
      if (ass) l.push(ass);
      return l.join("\n");
    }
  },

  agendamento_remarcado: {
    rotulo: "Agendamento remarcado",
    variaveis: ["cliente", "oficina", "data", "veiculo", "placa", "endereco", "telefone_oficina"],
    montar(dados, of) {
      const l = [];
      l.push(saudacao(dados, of));
      l.push("");
      l.push("Seu agendamento foi remarcado:");
      l.push("");
      if ((dados.dataHoraAnterior || "").trim()) l.push("Antes: " + dados.dataHoraAnterior.trim());
      l.push("Agora: " + (dados.dataHoraTexto || ""));
      const veic = linhaVeiculo(dados);
      if (veic) l.push("Veículo: " + veic);
      const end = ((of && of.endereco) || "").trim();
      if (end) l.push("Endereço: " + end);
      l.push("");
      l.push("Se o novo horário não for bom para você, me avise por aqui que remarcamos.");
      const ass = assinatura(of);
      if (ass) l.push(ass);
      return l.join("\n");
    }
  },

  agendamento_cancelado: {
    rotulo: "Agendamento cancelado",
    variaveis: ["cliente", "oficina", "data", "telefone_oficina"],
    montar(dados, of) {
      const l = [];
      l.push(saudacao(dados, of));
      l.push("");
      l.push("Seu agendamento do dia " + (dados.dataHoraTexto || "") + " foi cancelado.");
      l.push("");
      l.push("Quando quiser remarcar, é só responder por aqui que reservamos um novo horário.");
      const ass = assinatura(of);
      if (ass) l.push(ass);
      return l.join("\n");
    }
  },

  // ── PRÉ-ORÇAMENTO ────────────────────────────────────────────────────────
  // Leva os itens e o PREÇO FINAL no corpo. Nenhum campo de custo é lido.
  pre_orcamento: {
    rotulo: "Pré-orçamento",
    variaveis: ["cliente", "oficina", "veiculo", "placa", "itens", "total", "validade", "endereco", "telefone_oficina"],
    montar(dados, of) {
      const l = [];
      l.push(saudacao(dados, of));
      l.push("");
      const veic = linhaVeiculo(dados);
      l.push("Segue o pré-orçamento" + (veic ? " do seu " + veic : "") + ":");
      l.push("");
      const itens = listaItens(dados.itens);
      if (itens) l.push(itens);
      l.push("");
      l.push("Total: " + moeda(dados.total != null ? dados.total : totalItens(dados.itens)));
      if ((dados.validade || "").trim()) l.push("Validade: " + dados.validade.trim());
      l.push("");
      l.push("Os valores acima já são os finais, sem surpresa no fechamento.");
      l.push("Para aprovar ou tirar qualquer dúvida, é só responder por aqui.");
      if ((dados.link || "").trim()) {
        l.push("");
        l.push("Documento: " + dados.link.trim());
      }
      const ass = assinatura(of);
      if (ass) l.push("", ass);
      return l.join("\n");
    }
  },

  // ── SERVIÇO CONCLUÍDO / COMPROVANTE ──────────────────────────────────────
  os_concluida: {
    rotulo: "Serviço concluído",
    variaveis: ["cliente", "oficina", "os", "veiculo", "placa", "itens", "total", "endereco", "telefone_oficina"],
    montar(dados, of) {
      const l = [];
      l.push(saudacao(dados, of));
      l.push("");
      const veic = linhaVeiculo(dados);
      l.push("O serviço do seu " + (veic || "veículo") + " foi concluído.");
      if ((dados.osNumero || "").trim()) l.push("Ordem de serviço: " + dados.osNumero.trim());
      l.push("");
      const itens = listaItens(dados.itens);
      if (itens) {
        l.push("Serviços executados:");
        l.push(itens);
        l.push("");
      }
      l.push("Total: " + moeda(dados.total != null ? dados.total : totalItens(dados.itens)));
      l.push("");
      const end = ((of && of.endereco) || "").trim();
      if (end) l.push("Pode retirar em: " + end);
      l.push("Qualquer dúvida sobre o serviço, é só responder por aqui.");
      if ((dados.link || "").trim()) {
        l.push("");
        l.push("Comprovante: " + dados.link.trim());
      }
      const ass = assinatura(of);
      if (ass) l.push("", ass);
      return l.join("\n");
    }
  }
};

// ---------------------------------------------------------------------------
// MODELO PRÓPRIO DA OFICINA
//
// Guardado no navegador, por tenant e por tipo de evento. O motivo de não ir
// para o banco: POST /api/v1/config exige require_admin, ou seja, só o dono
// do SaaS pode gravar configuração -- a oficina não tem esse direito. Criar
// uma rota de escrita com escopo de tenant é mudança de backend e fica como
// etapa própria; até lá, o modelo vale naquele computador e o padrão
// continua valendo em qualquer outro.
// ---------------------------------------------------------------------------

function chaveModelo(tenantId, tipo) {
  return "whats_modelo_" + String(tenantId || "1") + "_" + tipo;
}

export function modeloSalvo(tenantId, tipo) {
  try {
    const v = localStorage.getItem(chaveModelo(tenantId, tipo));
    return v && v.trim() ? v : null;
  } catch (e) {
    return null;
  }
}

export function salvarModelo(tenantId, tipo, texto) {
  try {
    if (!texto || !texto.trim()) {
      localStorage.removeItem(chaveModelo(tenantId, tipo));
      return false;
    }
    localStorage.setItem(chaveModelo(tenantId, tipo), texto);
    return true;
  } catch (e) {
    return false;
  }
}

export function limparModelo(tenantId, tipo) {
  try {
    localStorage.removeItem(chaveModelo(tenantId, tipo));
  } catch (e) {
    /* navegador sem armazenamento: o padrão volta a valer sozinho */
  }
  return true;
}

// Substitui {variavel} pelo valor. Variável desconhecida fica como está, de
// propósito: assim um erro de digitação aparece na prévia em vez de sumir.
function aplicarVariaveis(modelo, vars) {
  return String(modelo).replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k] == null ? "" : vars[k]) : m));
}

function variaveisDe(dados, of) {
  const itens = Array.isArray(dados.itens) ? dados.itens : [];
  return {
    cliente: (dados.clienteNome || "").trim(),
    oficina: nomeOficina(of),
    endereco: ((of && of.endereco) || "").trim(),
    telefone_oficina: ((of && of.telefone) || "").trim(),
    data: dados.dataHoraTexto || "",
    data_anterior: dados.dataHoraAnterior || "",
    veiculo: dados.veiculo || "",
    placa: dados.placa || "",
    servico: (dados.servico || "").trim(),
    itens: listaItens(itens),
    total: moeda(dados.total != null ? dados.total : totalItens(itens)),
    validade: (dados.validade || "").trim(),
    os: (dados.osNumero || "").trim(),
    link: (dados.link || "").trim(),
  };
}

// ---------------------------------------------------------------------------
// montarMensagem
//
// Devolve:
//   tipo, rotulo      -- identificação do evento
//   texto             -- o que vai ser enviado (modelo da oficina, se houver)
//   textoPadrao       -- o molde de fábrica, para o botão "restaurar padrão"
//   temModelo         -- true quando a oficina tem modelo próprio salvo
//   destinatario      -- null quando o telefone não serve
//   variaveis         -- valores disponíveis, para a ajuda do editor
//   variaveisAceitas  -- nomes que fazem sentido neste tipo
//
// Falhas que podemos afirmar com honestidade, todas ANTES de qualquer envio:
//   - tipo inexistente  -> Error
//   - telefone inválido -> destinatario null
// Não há "status de entrega": quem envia é o WhatsApp do dono, na máquina
// dele. O sistema prepara; não entrega. Registrar status aqui seria gravar
// um dado que não possuímos.
// ---------------------------------------------------------------------------
export function montarMensagem(tipo, dados, oficina, tenantId) {
  const def = TIPOS[tipo];
  if (!def) throw new Error("mensagens: tipo desconhecido -> " + tipo);
  const d = dados || {};
  const of = oficina || {};
  const textoPadrao = def.montar(d, of);
  const modelo = tenantId != null ? modeloSalvo(tenantId, tipo) : null;
  const vars = variaveisDe(d, of);
  return {
    tipo,
    rotulo: def.rotulo,
    texto: modelo ? aplicarVariaveis(modelo, vars) : textoPadrao,
    textoPadrao,
    temModelo: !!modelo,
    modelo: modelo || "",
    destinatario: telefoneWhats(d.telefone),
    variaveis: vars,
    variaveisAceitas: def.variaveis || [],
  };
}

// Prévia de um modelo que o dono está editando, sem salvar nada.
export function previaModelo(modelo, dados, oficina) {
  return aplicarVariaveis(modelo, variaveisDe(dados || {}, oficina || {}));
}

// ---------------------------------------------------------------------------
// CANAIS
//
// "web" -> web.whatsapp.com/send   abre o WhatsApp Web direto
// "app" -> whatsapp://send         abre o aplicativo instalado, direto
//
// https://wa.me/ NÃO é usado. Ele é uma página de redirecionamento da Meta:
// no desktop sempre para e pergunta "Abrir app / Continuar para o WhatsApp
// Web". Essa parada é a perda de experiência que este módulo remove.
//
// whatsapp:// sozinho também não serve como único caminho: se o aplicativo
// não estiver instalado ele falha em silêncio -- nada abre, nenhum erro
// detectável. Por isso o canal é uma escolha visível do dono da oficina,
// lembrada por tenant, e nunca um palpite do sistema.
// ---------------------------------------------------------------------------

export const CANAIS = ["web", "app"];
export const CANAL_PADRAO = "web";

function chaveCanal(tenantId) {
  return "whats_canal_tenant_" + String(tenantId || "1");
}

export function canalPreferido(tenantId) {
  try {
    const v = localStorage.getItem(chaveCanal(tenantId));
    return CANAIS.indexOf(v) >= 0 ? v : CANAL_PADRAO;
  } catch (e) {
    return CANAL_PADRAO;
  }
}

export function salvarCanal(tenantId, canal) {
  if (CANAIS.indexOf(canal) < 0) return CANAL_PADRAO;
  try { localStorage.setItem(chaveCanal(tenantId), canal); } catch (e) {}
  return canal;
}

export function urlWhats(destinatario, texto, canal) {
  if (!destinatario) return null;
  const q = "phone=" + encodeURIComponent(destinatario) + "&text=" + encodeURIComponent(texto || "");
  return canal === "app"
    ? "whatsapp://send?" + q
    : "https://web.whatsapp.com/send?" + q;
}

// Abre o WhatsApp no canal escolhido. Devolve true só quando havia
// destinatário válido e a janela foi solicitada -- true significa
// "preparado", nunca "entregue".
export function abrirWhats(destinatario, texto, canal) {
  const url = urlWhats(destinatario, texto, canal);
  if (!url) return false;
  if (canal === "app") {
    // Protocolo próprio: window.open costuma ser bloqueado ou deixar aba
    // órfã em branco. location.href entrega ao SO sem abrir aba.
    window.location.href = url;
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
  return true;
}
